import './style.scss';

import { useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import { Message } from 'primereact/message';
import dayjs from 'dayjs';
import { getRegisterResultByProjectId } from 'apis/registerResult.api';
import { getProjectById } from 'apis/projects.api';
import RegisterResultComment from 'features/SubmitProjectProposal/components/RegisterResultComment';
import {
  useChangeRegisterResultStatus,
  useCreateRegisterResultReviews,
  useDownloadRegisterResultDocxFile,
  useDownloadRegisterResultFileUpload,
  useDownloadRegisterResultPdfFile,
  useGetRegisterResultReviewHistories,
  useGetRegisterResultReviews,
  useUpdateRegisterResultReviews,
} from 'utils/hooks/registerResults';
import { toast } from 'layout';
import { REGISTER_RESULT_STATUSES, TOAST } from 'constant';
import { autoDownloadFile, getEndingTimeOfDate, mapError } from 'utils/func';
import { registerResultCommentsSchema } from 'features/SubmitProjectProposal/validation';
import RegisterResultReviewHistory from 'features/SubmitProjectProposal/components/RegisterResultReviewHistory';
import GeneralInfo from './GeneralInfo';
import RegisterContent from './RegisterContent';
import ExpenditureAndAppendix from './ExpenditureAndAppendix';
import RejectRegisterResultModal from './RejectRegisterResultModal';

export default function RegisterResultChecker({ isLiquidation, isSumup }) {
  const { t } = useTranslation();
  const { projectId } = useParams();

  const [visible, setVisible] = useState(false);
  const [isCommentVisible, setIsCommentVisible] = useState(false);
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [schema, setSchema] = useState(registerResultCommentsSchema);

  // data project
  const { data: _dataProject } = useQuery({
    queryKey: ['dataProject'],
    queryFn: () => getProjectById(projectId),
  });
  const data = useMemo(() => _dataProject?.data ?? {}, [_dataProject]);

  const { control, handleSubmit, setValue, reset, getValues } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });
  const { errors, isDirty, dirtyFields } = useFormState({ control });

  const { data: _registerResult, refetch: refetchRegisterResult } = useQuery({
    queryKey: ['registerResult'],
    queryFn: () => getRegisterResultByProjectId(projectId),
  });

  const registerResult = useMemo(() => _registerResult?.data ?? {}, [_registerResult]);

  const { data: registerResultComment, refetch: refetchRegisterResultComment } =
    useGetRegisterResultReviews(registerResult?.id, {
      enabled: !!registerResult?.id,
    });

  const latestComment = useMemo(
    () => registerResultComment?.[(registerResultComment?.length || 1) - 1] ?? null,
    [registerResultComment]
  );

  const { data: registerResultReviewHistories, refetch: refetchRegisterResultReviewHistories } =
    useGetRegisterResultReviewHistories(registerResult?.id, { enabled: !!registerResult?.id });

  const registerResultReviewHistoriesUsed = useMemo(() => {
    const result = [];
    let temp = [];
    registerResultReviewHistories?.forEach((item) => {
      if (item.action === 'reset') {
        result.push(temp);
        temp = [];
        temp.push(item);
      } else {
        temp.push(item);
      }
    });
    result.push(temp);
    return result;
  }, [registerResultReviewHistories]);

  const { mutateAsync: createRegisterResultReview } = useCreateRegisterResultReviews();
  const { mutateAsync: updateRegisterResultReview } = useUpdateRegisterResultReviews();
  const { mutateAsync: downloadRegisterResultFile } = useDownloadRegisterResultFileUpload();
  const { mutateAsync: mutateAsyncChangeStatus } = useChangeRegisterResultStatus();

  useEffect(() => {
    if (registerResult) {
      // ParticipantTable
      if (registerResult?.registerResultAuthors) {
        setValue('registerResultAuthors', registerResult?.registerResultAuthors ?? []);
      }

      // SecurtityInformation
      setValue('securitySeverity', registerResult?.securitySeverity ?? '');

      // Product
      setValue('summaryReportQuantity.value', registerResult?.statistics?.summaryReportQuantity);
      setValue(
        'branchProposalReportQuantity.value',
        registerResult?.statistics?.branchProposalReportQuantity
      );
      setValue('thematicReportQuantity.value', registerResult?.statistics?.thematicReportQuantity);
      setValue('mapQuantity.value', registerResult?.statistics?.mapQuantity);
      setValue('filmQuantity.value', registerResult?.statistics?.filmQuantity);
      setValue('videoQuantity.value', registerResult?.statistics?.videoQuantity);
      setValue('imageQuantity.value', registerResult?.statistics?.imageQuantity);
      setValue('cdDiskQuantity.value', registerResult?.statistics?.cdDiskQuantity);
      setValue('documentQuantity.value', registerResult?.statistics?.documentQuantity);
      setValue('other.value', registerResult?.statistics?.other);

      // Product Description
      setValue('registerResultStats', registerResult?.products ?? []);

      setValue('note', registerResult?.note ?? '');
      setValue('settlementAmount', registerResult?.settlementAmount ?? 0);
      setValue('savingsAmount', registerResult?.savingsAmount ?? 0);
      setValue('recoveryAmount', registerResult?.recoveryAmount ?? 0);
    }
  }, [registerResult]);

  useEffect(() => {
    if (
      !latestComment ||
      (latestComment && latestComment?.feedback && dayjs().isAfter(dayjs(latestComment?.deadline)))
    ) {
      setValue('isEditComment', false);
    } else if (latestComment) {
      setValue('deadline', new Date(latestComment?.deadline ?? null));
      setValue('isEditComment', true);
    }
  }, [latestComment]);

  const onSubmitComment = async (body) => {
    if (!body?.comment?.trim()) {
      toast(TOAST.ERROR, 'Vui lòng nhập nhận xét');
      return;
    }

    const dataSender = {
      comment: body?.comment || '',
      deadline: getEndingTimeOfDate(body?.deadline) || null,
      editable: true,
    };
    const isEditComment = body?.isEditComment || false;
    try {
      if (!isEditComment) {
        await createRegisterResultReview({
          id: registerResult?.id,
          body: dataSender,
        });
        toast(TOAST.SUCCESS, 'Yêu cầu cập nhật thành công');
      }
      if (isEditComment) {
        await updateRegisterResultReview({
          id: registerResult?.id,
          reviewId: latestComment?.id,
          body: dataSender,
        });
        toast(TOAST.SUCCESS, 'Yêu cầu cập nhật thành công');
      }
      refetchRegisterResultComment();
      refetchRegisterResult();
      refetchRegisterResultReviewHistories();
      reset({}, { keepValues: true });
    } catch (error) {
      // Handle error if needed
    }
  };

  const handleDownloadFile = async (opts) => {
    const { filename, type } = opts;
    await downloadRegisterResultFile({
      id: registerResult?.id,
      filename,
      type,
    });
  };

  const onCloseRejectModal = () => {
    reset({}, { keepValues: true });
    setSchema(registerResultCommentsSchema);
    setRejectModalVisible(false);
  };

  const onSubmit = async (body) => {
    const dataSender = {};
    if (rejectModalVisible) {
      dataSender.action = REGISTER_RESULT_STATUSES.REJECTED;
      dataSender.rejectReason = body?.rejectReason;
    } else if (approveModalVisible) {
      dataSender.action = REGISTER_RESULT_STATUSES.APPROVED;
    }
    try {
      const res = await mutateAsyncChangeStatus({ id: registerResult?.id, body: dataSender });
      if (res?.status.toString().startsWith('2')) {
        toast(
          TOAST.SUCCESS,
          t(`registerResult.${rejectModalVisible ? 'rejectModal' : 'approveModal'}.success`)
        );
        refetchRegisterResult();
        refetchRegisterResultReviewHistories();
        setSchema(registerResultCommentsSchema);
        setRejectModalVisible(false);
        setApproveModalVisible(false);
      }
    } catch (error) {
      toast(TOAST.ERROR, mapError(t, error));
    }
  };

  const footerConfirmSubmit = () => (
    <div>
      <Button
        label={t('formLayout.action.ok')}
        className="w-7rem"
        severity="info"
        type="button"
        onClick={onSubmit}
      />
    </div>
  );

  const {
    mutateAsync: downloadDocxFile,
    isLoading: isDownloadDocxFileLoading,
    isFetching: isDownloadDocxFileFetching,
  } = useDownloadRegisterResultDocxFile();
  const {
    mutateAsync: downloadPdfFile,
    isLoading: isDownloadPdfFileLoading,
    isFetching: isDownloadPdfFileFetching,
  } = useDownloadRegisterResultPdfFile();

  const handleDownloadDocxFile = async () => {
    const res = await downloadDocxFile({ projectId });
    autoDownloadFile(res);
  };

  const handleDownloadPdfFile = async () => {
    const res = await downloadPdfFile({ projectId });
    autoDownloadFile(res);
  };

  return (
    <div className="review-form p-5">
      <div className="flex justify-content-end mb-4">
        <Button
          type="button"
          label={t('registerResult.reviewHistory.title')}
          icon="pi pi-history"
          className="block w-auto h-3rem mr-4"
          onClick={() => setVisible(true)}
        />

        <Button
          type="button"
          label={t('registerResult.download.exportDocx')}
          icon={`pi ${
            isDownloadDocxFileLoading || isDownloadDocxFileFetching
              ? 'pi-spin pi-spinner'
              : 'pi-file-word'
          }`}
          className="block w-auto h-3rem mr-4"
          severity="info"
          onClick={handleDownloadDocxFile}
          disabled={isDownloadDocxFileLoading || isDownloadDocxFileFetching}
        />
        <Button
          type="button"
          label={t('registerResult.download.exportPdf')}
          icon={`pi ${
            isDownloadPdfFileLoading || isDownloadPdfFileFetching
              ? 'pi-spin pi-spinner'
              : 'pi-file-pdf'
          }`}
          className="block w-auto h-3rem border-none"
          severity="danger"
          onClick={handleDownloadPdfFile}
          disabled={isDownloadPdfFileLoading || isDownloadPdfFileFetching}
        />
      </div>
      <div className="mb-5">
        <div className="text-center">
          <p className="font-bold text-2xl my-0">{t('r09.title1')}</p>
          <p className=" text-2xl my-0">{t('r09.title2')}</p>
        </div>
      </div>
      <div className="p-fluid form-midterm-container">
        <GeneralInfo data={data} isLiquidation={isLiquidation} />
        <div className="my-4">
          <RegisterContent
            disabled
            control={control}
            errors={errors}
            viewOnly
            registerResult={registerResult}
            registerResultId={registerResult?.id}
            participants={data?.projectProposal?.projectProposalContacts}
            handleDownloadFile={handleDownloadFile}
            isLiquidation={isLiquidation}
          />
        </div>

        <div className="mt-4">
          <ExpenditureAndAppendix
            disabled
            control={control}
            errors={errors}
            appendixeArray={registerResult?.appendixes || []}
            minuteFile={registerResult?.minuteFile}
            handleDownloadFile={handleDownloadFile}
            isLiquidation={isLiquidation}
          />
        </div>

        <RegisterResultComment
          visible={isCommentVisible}
          onClose={() => setIsCommentVisible(false)}
          control={control}
          errors={errors}
          handleSubmit={handleSubmit}
          onSubmit={onSubmitComment}
          data={registerResultComment}
          status={registerResult?.status}
          disabled={!(isDirty && Object.keys(dirtyFields).length)}
          isEditComment={getValues('isEditComment')}
          isSumup={isSumup}
          setApproveModalVisible={setApproveModalVisible}
          setVisible={setVisible}
        />
      </div>
      <RejectRegisterResultModal
        visible={rejectModalVisible}
        onClose={onCloseRejectModal}
        control={control}
        errors={errors}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        isDeadline={false}
      />

      <Dialog
        visible={approveModalVisible}
        header={<p className="text-blue-500">{t('registerResult.dialogConfirm.title')}</p>}
        position="center"
        style={{ width: '410px' }}
        onHide={() => setApproveModalVisible(false)}
        footer={() => footerConfirmSubmit()}
      >
        <Message severity="info" text={t('registerResult.dialogConfirm.content')} />
        <Message
          severity="warn"
          text={t('registerResult.dialogConfirm.attention')}
          className="mt-3"
        />
      </Dialog>

      <RegisterResultReviewHistory
        data={registerResultReviewHistoriesUsed || []}
        visible={visible}
        setVisible={setVisible}
        title={t('registerResult.reviewHistory.title')}
      />
    </div>
  );
}

RegisterResultChecker.propTypes = {
  isLiquidation: PropTypes.bool,
  isSumup: PropTypes.bool,
};

RegisterResultChecker.defaultProps = {
  isLiquidation: false,
  isSumup: false,
};
